import { useEffect, useState } from 'react';
import {
  ENTITY_TYPES,
  COMPANY_STATES,
  COMPANY_STATE_LABEL,
  ENTITY_TYPE_LABEL,
  PROFILE_TYPES,
  PROFILE_TYPE_LABEL,
  ROLES,
  ROLE_LABEL,
  STAFF_ROLES,
  STAFF_ROLE_HINT,
  STAFF_ROLE_LABEL,
  VERIFY_STATES,
  VERIFY_STATE_LABEL,
  USER_SORTS,
  USER_SORT_LABEL,
  type AdminCompanyRow,
  type AdminStats,
  type AdminSystemStatus,
  type AdminUserRow,
  type CompanyState,
  type EntityType,
  type ProfileType,
  type Role,
  type StaffRole,
  type UserSort,
  type VerifyState,
} from 'shared';
import AdminCompanyMembers from '../AdminCompanyMembers';
import AdminLog from './AdminLog';
import AdminCurrencies from './AdminCurrencies';
import AdminNav, { ADMIN_TAB_LABEL, type AdminTab } from './AdminNav';
import { useNavWide } from '../navWidth';
import { api, startViewAs } from '../api';
import Icon from '../Icon';
import { alert, confirm, haptic } from '../telegram';
import AdminGroups from './AdminGroups';
import AdminContracts from './AdminContracts';
import AdminSettings from './AdminSettings';
import AdminIntegrations from './AdminIntegrations';
import AdminFeatures from './AdminFeatures';
import AdminCargos from './AdminCargos';
import AdminBroadcast from './AdminBroadcast';
import AdminIngest from './AdminIngest';
import AdminList from '../AdminList';
import AdminFilters from '../AdminFilters';
import AdminSheet from '../AdminSheet';

type Tab = AdminTab;

/**
 * Когда человек был в приложении — датой и временем.
 *
 * Раньше здесь стояли ровные слова: «2 дн назад», «больше месяца». Они
 * отвечают на вопрос «давно ли», но не отвечают на «когда именно», а при
 * разборе обращения нужно как раз второе: жалоба приходит с датой, и
 * сверять её с «дн назад» приходится в уме.
 *
 * Год не показываем: в списке он одинаков у всех строк и съедает ширину,
 * которой на телефоне и так нет. Полная дата — в подсказке.
 */
function sinceLabel(iso: string | null): string {
  if (!iso) return 'не заходил';

  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Диск и резервные копии — на порядок больше файла заявки, счёт на
 * гигабайты: `Files.tsx` считает мегабайтами не просто так, там потолок
 * загрузки 10 МБ, здесь счёт другой. */
function fmtBytes(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(1)} ГБ`;
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} МБ`;
}

/** То же «дата и время», что и у активности участников — жалоба на
 * пропавшую копию приходит с датой, а не со словами «вчера». */
function whenLabel(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type Tone = 'ok' | 'warn' | 'bad';

/** Копия раз в сутки, проверка восстановлением — раз в неделю (см.
 * `CLAUDE.md`, раздел «Резервные копии»). Запас перед тем, как считать
 * копию просроченной: таймер мог сработать на час позже из-за
 * `RandomizedDelaySec`, и ровно сутки — это уже ложная тревога. */
const BACKUP_STALE_HOURS = 30;
const VERIFY_STALE_HOURS = 24 * 9;

function hoursSince(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}

function seenTip(u: AdminUserRow): string {
  const full = (iso: string) => new Date(iso).toLocaleString('ru-RU');
  const seen = u.lastSeenAt
    ? `Последняя активность: ${full(u.lastSeenAt)}`
    : 'Активности не было';
  const login = u.lastLoginAt ? `\nВход: ${full(u.lastLoginAt)}` : '\nВходов не было';
  return seen + login;
}

export default function Admin({
  tab,
  onTab,
  onOpenDraft,
}: {
  /** Выбранный раздел. Им владеет App: пункты стоят в боковом меню. */
  tab: Tab | null;
  onTab: (t: Tab | null) => void;
  /** Открыть форму публикации с черновиком (для «Импорта из чатов»). */
  onOpenDraft?: (draftId: string) => void;
}) {
  /*
   * Выбранный раздел. На телефоне пусто — там сначала показывается меню,
   * как в системных настройках: список разделов сам по себе экран, и
   * «назад» ведёт к нему. На широком экране раздел всегда выбран, потому
   * что рельс разделов и так виден рядом.
   */
  /*
   * Разделы админки живут вложенным списком в боковом меню. Свёрнуто оно
   * или нет — решает человек, и при свёрнутом раздел нечем сменить:
   * поэтому админка показывает свой экран-меню, как на телефоне.
   */
  const wide = useNavWide();
  const pick = (t: Tab | null) => {
    haptic();
    onTab(t);
  };
  // Раздел не выбран — показываем сводку по площадке, а не подставляем
  // «Пользователей»: их поиск и сортировки живут в своей вкладке, на
  // посадочной им не место.
  const openTab: Tab | null = tab ?? null;
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [system, setSystem] = useState<AdminSystemStatus | null>(null);

  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [q, setQ] = useState('');

  /*
   * Фильтры повторяют ленту грузов: чипы для частого, панель для остального.
   * Логика уже знакома по «Найти груз», и второй раз ей учиться не приходится.
   */
  const [openFilters, setOpenFilters] = useState(false);
  const [sort, setSort] = useState<UserSort>('new');
  const [role, setRole] = useState<Role | ''>('');
  const [profileType, setProfileType] = useState<ProfileType | ''>('');
  const [entityType, setEntityType] = useState<EntityType | ''>('');
  const [staffRole, setStaffRole] = useState<StaffRole | ''>('');
  const [verifyState, setVerifyState] = useState<VerifyState | ''>('');
  const [banned, setBanned] = useState<'' | 'yes' | 'no'>('');
  const [active, setActive] = useState<'' | 'yes' | 'no'>('');
  const [withCompany, setWithCompany] = useState(false);
  const [activeDays, setActiveDays] = useState<number | ''>('');

  const [companies, setCompanies] = useState<AdminCompanyRow[]>([]);
  /*
   * Каких компаний показывать. Пусто — все, и это важно: вкладка
   * открывалась с включённым «только непроверенные», администратор видел
   * часть списка, не зная об этом, и «компании пропали» было обычной
   * жалобой.
   */
  const [companyState, setCompanyState] = useState<CompanyState>('');
  const [companyQuery, setCompanyQuery] = useState('');

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  /**
   * Вкладка внутри развёрнутой карточки — одна на весь экран, а не на
   * пользователя: раскрыт всегда только один человек, а «Профиль» как
   * стартовая вкладка должен быть одним и тем же при каждом раскрытии.
   */
  const [userTab, setUserTab] = useState<'profile' | 'contacts' | 'access'>('profile');

  /*
   * Пароль, который администратор задаёт вручную.
   *
   * Одно поле на раскрытую карточку: иначе набранный для одного человека
   * пароль остаётся в поле, когда открываешь другого, и уходит не туда.
   */
  const [newPassword, setNewPassword] = useState('');

  /**
   * Кого сейчас разбираем.
   *
   * Берём из загруженного списка по идентификатору, а не храним копию:
   * список перечитывается после каждого действия, и вторая копия
   * показывала бы состояние до правки — забанил, а в окне «не забанен».
   */
  const openUser = users.find((u) => u.id === expanded) ?? null;
  const openCompany = companies.find((c) => c.id === expanded) ?? null;

  /** Сколько фильтров применено — для подписи на кнопке. */
  const userFilters = [
    role,
    profileType,
    entityType,
    staffRole,
    verifyState,
    banned,
    active,
    withCompany ? '1' : '',
    activeDays ? String(activeDays) : '',
  ].filter(Boolean).length;

  async function loadStats() {
    try {
      setStats(await api.admin.stats());
    } catch (e) {
      alert((e as Error).message);
    }
  }

  // Молча: это диагностика для главной страницы, а не действие человека —
  // всплывающая ошибка о недоступности статуса площадки была бы более
  // навязчивой, чем сам факт, что статус сейчас не известен.
  async function loadSystem() {
    try {
      setSystem(await api.admin.system());
    } catch {
      setSystem(null);
    }
  }

  async function loadUsers(append = false) {
    if (append) setMore(true);
    else setLoading(true);

    try {
      const res = await api.admin.users({
        q: q || undefined,
        sort,
        role: role || undefined,
        profileType: profileType || undefined,
        entityType: entityType || undefined,
        staffRole: staffRole || undefined,
        verifyState: verifyState || undefined,
        banned: banned === '' ? undefined : banned === 'yes' ? 1 : 0,
        active: active === '' ? undefined : active === 'yes' ? 1 : 0,
        withCompany: withCompany ? 1 : undefined,
        activeDays: activeDays || undefined,
        limit: 40,
        ...(append && cursor ? { cursor } : {}),
      });

      setUsers((prev) => (append ? [...prev, ...res.items] : res.items));
      setTotal(res.total);
      setCursor(res.nextCursor);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
      setMore(false);
    }
  }

  async function loadCompanies() {
    setLoading(true);
    try {
      setCompanies((await api.admin.companies(companyState, companyQuery)).items);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadStats();
    void loadSystem();
  }, []);

  useEffect(() => {
    if (tab === 'users') void loadUsers();
    else if (tab === 'companies') void loadCompanies();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    sort,
    role,
    profileType,
    entityType,
    staffRole,
    banned,
    active,
    withCompany,
    activeDays,
    companyState,
    companyQuery,
  ]);

  // Поиск с задержкой: иначе запрос уходит на каждую букву.
  useEffect(() => {
    if (tab !== 'users') return;
    const t = setTimeout(() => void loadUsers(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  async function patchUser(u: AdminUserRow, data: Parameters<typeof api.admin.updateUser>[1]) {
    setBusy(u.id);
    try {
      const updated = await api.admin.updateUser(u.id, data);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      void loadStats();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /** Контакты сохраняются по уходу из поля — отдельная кнопка тут лишняя. */
  async function patchContacts(
    u: AdminUserRow,
    data: { login?: string; email?: string; phone?: string; tgUsername?: string },
  ) {
    setBusy(u.id);
    try {
      const updated = await api.admin.updateContacts(u.id, data);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
    } catch (e) {
      alert((e as Error).message);
      // Значение в поле осталось новым, а на сервере — старое.
      // Перечитываем список, чтобы форма не врала.
      void loadUsers();
    } finally {
      setBusy(null);
    }
  }

  async function toggleBan(u: AdminUserRow) {
    if (u.banned) {
      if (!(await confirm(`Снять блокировку с ${u.login ?? u.firstName}?`))) return;
      return patchUser(u, { banned: false });
    }

    if (!(await confirm(`Заблокировать ${u.login ?? u.firstName}?`))) return;
    const reason = window.prompt('Причина блокировки (увидит пользователь):') ?? '';
    return patchUser(u, { banned: true, banReason: reason || undefined });
  }

  /**
   * Допуск на площадку: одобрить заявку (в т.ч. после модерации) или
   * деактивировать уже одобренного. В отличие от бана — без причины,
   * это не наказание, а рубильник допуска.
   */
  async function toggleActive(u: AdminUserRow) {
    if (u.active) {
      if (!(await confirm(`Деактивировать ${u.login ?? u.firstName}?`))) return;
      return patchUser(u, { active: false });
    }

    if (!(await confirm(`Активировать ${u.login ?? u.firstName}?`))) return;
    return patchUser(u, { active: true });
  }

  /**
   * Задать конкретный пароль.
   *
   * Отдельно от сброса: сброс выдаёт случайный, когда человек свой забыл,
   * а это — когда учётку заводят вручную или помогают войти по телефону.
   */
  async function setPassword(u: AdminUserRow) {
    if (newPassword.length < 8) {
      await alert('Пароль от 8 символов.');
      return;
    }

    const who = u.login ?? [u.firstName, u.lastName].filter(Boolean).join(' ');
    if (!(await confirm(`Задать новый пароль для ${who || 'пользователя'}?`))) return;

    setBusy(u.id);
    try {
      const res = await api.admin.setPassword(u.id, newPassword);
      // Показываем один раз: в базе лежит только хеш, повторно не достать.
      await alert(
        `Пароль изменён.

Логин: ${res.login}
Пароль: ${newPassword}

` +
          'Передайте его лично: в переписке пароли не отправляем.',
      );
      setNewPassword('');
      await loadUsers();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Решение по ручной проверке.
   *
   * Отказ требует причины: человек должен понимать, что исправить, иначе
   * подаёт заявку снова и снова, а поддержка разбирает одно и то же.
   */
  async function decideVerify(u: AdminUserRow, state: VerifyState) {
    let note: string | undefined;

    if (state === 'REJECTED') {
      const reason = window.prompt('Причина отказа — её увидит участник:');
      if (!reason?.trim()) return;
      note = reason.trim();
    }

    setBusy(u.id);
    try {
      await api.admin.verify(u.id, state, note);
      await loadUsers();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function resetPassword(u: AdminUserRow) {
    if (!(await confirm(`Сбросить пароль для ${u.login}?`))) return;
    setBusy(u.id);
    try {
      const res = await api.admin.resetPassword(u.id);
      // Показываем один раз: в базе лежит только хеш, повторно не достать.
      alert(`Новый пароль для ${res.login}:\n\n${res.password}\n\nПередайте его лично.`);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Посмотреть площадку глазами участника.
   *
   * Половина обращений — «не вижу заявку», «пустая аналитика» — про права
   * и фильтры, а не про данные. Смотреть на них из чужой базы бессмысленно:
   * проще один раз увидеть тот же экран.
   */
  async function viewAs(u: AdminUserRow) {
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.login;
    if (!(await confirm(`Открыть приложение глазами ${name}? Менять ничего нельзя.`))) {
      return;
    }

    setBusy(u.id);
    try {
      const res = await api.admin.viewAs(u.id);
      startViewAs(res.token);
      // Перезагрузка, а не обновление состояния: меняются права, разделы
      // и содержимое каждого экрана — собрать это на лету дороже.
      window.location.reload();
    } catch (e) {
      await alert((e as Error).message);
      setBusy(null);
    }
  }

  async function patchCompany(
    c: AdminCompanyRow,
    data: { verified?: boolean; isDirectShipper?: boolean },
  ) {
    setBusy(c.id);
    try {
      const updated = await api.admin.updateCompany(c.id, data);
      setCompanies((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...updated } : x)));
      void loadStats();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /*
   * Плитка сводки — это вход в свой раздел, а не просто число: те же
   * участники, заявки и компании живут в своих вкладках, и плитка ведёт
   * туда же, где их и разбирают. У «рейсов в пути» отдельной вкладки нет
   * — остаётся неактивным числом.
   */
  const statTile = (
    value: string | number,
    label: string,
    to: Tab | null,
    color?: string,
  ) => {
    const inner = (
      <>
        <div className="price" style={{ fontSize: 19, color }}>
          {value}
        </div>
        <div className="meta">{label}</div>
      </>
    );
    return to ? (
      <button type="button" className="stat-grid__link" onClick={() => pick(to)}>
        {inner}
      </button>
    ) : (
      <div>{inner}</div>
    );
  };

  const statsCard = stats ? (
    <div className="card">
      <div className="stat-grid">
        {statTile(stats.users.total, 'пользователей', 'users')}
        {statTile(`+${stats.users.newWeek}`, 'за неделю', 'users')}
        {statTile(
          stats.users.banned,
          'в бане',
          'users',
          stats.users.banned ? 'var(--danger)' : undefined,
        )}
        {statTile(stats.cargos.active, 'активных грузов', 'cargos')}
        {statTile(stats.orders.active, 'рейсов в пути', null)}
        {statTile(
          stats.companies.unverified,
          'компаний на проверке',
          'companies',
          stats.companies.unverified ? 'var(--warn)' : undefined,
        )}
      </div>
    </div>
  ) : null;

  /*
   * Состояние площадки — эксплуатация, а не бизнес-показатели, и живёт
   * отдельной карточкой, а не строкой в `statsCard`: молчащий бот или
   * забитый диск не должны теряться среди чисел про заявки и участников,
   * а рядом с ними и не должны повторяться на каждой вкладке — это
   * диагностика для входа в админку, а не рабочий инструмент раздела.
   *
   * Три состояния через уже принятые в проекте бейджи: зелёный
   * (`badge--direct`) — в порядке, оранжевый (`badge--warn`) — стоит
   * посмотреть, красный (`badge--danger`) — не работает. Отсутствие
   * данных — не то же самое, что «плохо»: локальная разработка и машина
   * без резервного копирования не должны выглядеть тревожно только
   * потому, что канал не настроен.
   */
  const diskTone: Tone | null = system?.disk
    ? system.disk.usedPercent >= 90
      ? 'bad'
      : system.disk.usedPercent >= 75
        ? 'warn'
        : 'ok'
    : null;

  const backupTone: Tone | null = system?.backup
    ? !system.backup.ok
      ? 'bad'
      : hoursSince(system.backup.ts) > BACKUP_STALE_HOURS
        ? 'warn'
        : 'ok'
    : null;

  const verifyTone: Tone | null = system?.verify
    ? !system.verify.ok
      ? 'bad'
      : hoursSince(system.verify.ts) > VERIFY_STALE_HOURS
        ? 'warn'
        : 'ok'
    : null;

  const toneBadge = (tone: Tone | null, okText: string, other: string) =>
    tone ? (
      <span
        className={`badge ${tone === 'ok' ? 'badge--direct' : tone === 'warn' ? 'badge--warn' : 'badge--danger'}`}
      >
        {tone === 'ok' ? okText : other}
      </span>
    ) : (
      <span className="meta">нет данных</span>
    );

  const systemCard = system ? (
    <div className="card sys-status">
      <h3>Состояние площадки</h3>

      {/* Бейдж в строке рядом с названием, подробности — отдельной строкой
          снизу: на телефоне «04.09, 01:31 · участников 60, заявок 50» рядом
          с бейджем не поместилось бы и обрезалось бы посередине слова. */}
      <div className="row">
        <span className="meta">Бот</span>
        {system.bot.online ? (
          <span className="badge badge--direct">
            работает{system.bot.username ? ` · @${system.bot.username}` : ''}
          </span>
        ) : (
          <span className="badge badge--danger">не отвечает</span>
        )}
      </div>
      {!system.bot.online && system.bot.error && (
        <div className="meta sys-status__detail">{system.bot.error}</div>
      )}

      <div className="row">
        <span className="meta">Диск</span>
        {toneBadge(diskTone, `${system.disk?.usedPercent}%`, `${system.disk?.usedPercent}%`)}
      </div>
      {system.disk && (
        <div className="meta sys-status__detail">
          {fmtBytes(system.disk.usedBytes)} из {fmtBytes(system.disk.totalBytes)}
        </div>
      )}

      <div className="row">
        <span className="meta">Резервная копия</span>
        {toneBadge(backupTone, 'свежая', system.backup?.ok ? 'просрочена' : 'сбой')}
      </div>
      {system.backup && (
        <div className="meta sys-status__detail">
          {whenLabel(system.backup.ts)} · {fmtBytes(system.backup.sizeBytes)}
        </div>
      )}

      <div className="row">
        <span className="meta">Проверка восстановлением</span>
        {toneBadge(verifyTone, 'пройдена', system.verify?.ok ? 'просрочена' : 'сбой')}
      </div>
      {system.verify && (
        <div className="meta sys-status__detail">
          {whenLabel(system.verify.ts)}
          {system.verify.ok && system.verify.users !== null
            ? ` · участников ${system.verify.users}, заявок ${system.verify.cargos}`
            : ''}
        </div>
      )}
    </div>
  ) : null;

  // Раздел ещё не выбран: посадочная — сводка по площадке. Список
  // разделов (`AdminNav`) нужен только на телефоне; на широком экране они
  // стоят в боковом меню приложения.
  if (!openTab) {
    return (
      <>
        <h2>Администрирование</h2>
        {statsCard}
        {systemCard}
        {!wide && <AdminNav onPick={pick} />}
      </>
    );
  }

  return (
    <div className="admin">
      <div className="admin__body">
        {/* На телефоне возврат к списку разделов — обычная ссылка назад,
            а не жест: жестами в Mini App распоряжается Telegram. */}
        {!wide && (
          <button type="button" className="link-back" onClick={() => pick(null)}>
            <Icon name="arrow" size={15} /> Разделы
          </button>
        )}

        <h2>{ADMIN_TAB_LABEL[openTab]}</h2>
        {/* Сводка по площадке (statsCard / systemCard) — только на
            посадочной «Администрирование», не на каждой вкладке: над
            списком контрактов или настроек она была бы чистым шумом. */}


      {openTab === 'groups' && <AdminGroups />}
      {openTab === 'rules' && <AdminContracts />}
      {openTab === 'cargos' && <AdminCargos />}
      {openTab === 'broadcast' && <AdminBroadcast />}
      {openTab === 'ingest' && <AdminIngest onOpenDraft={onOpenDraft} />}
      {openTab === 'features' && <AdminFeatures />}
      {openTab === 'settings' && <AdminSettings />}
      {openTab === 'integrations' && <AdminIntegrations />}
      {openTab === 'log' && <AdminLog />}
      {openTab === 'currencies' && <AdminCurrencies />}

      {openTab === 'users' && (
        <>
          <div className="searchbar">
            <input
              placeholder="Логин, имя, телефон, почта, ИНН, @username"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Поиск участника"
            />
          </div>

          <div className="chips" style={{ marginTop: 'var(--s3)' }}>
            {USER_SORTS.map((sv) => (
              <button
                key={sv}
                className={sort === sv ? 'chip chip--on' : 'chip'}
                onClick={() => setSort(sv)}
              >
                {USER_SORT_LABEL[sv]}
              </button>
            ))}
            <button
              className={
                openFilters || userFilters > 0
                  ? 'chip chip--applied'
                  : 'chip chip--ghost'
              }
              onClick={() => setOpenFilters((v) => !v)}
            >
              <Icon name="filter" size={14} />
              Фильтры{userFilters > 0 ? ` · ${userFilters}` : ''}
            </button>
          </div>

          {openFilters && (
            <div className="card card--inset" style={{ marginTop: 'var(--s3)' }}>
              <div className="field-row">
                <div className="field">
                  <label>Роль</label>
                  <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                    <option value="">Любая</option>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>Профиль</label>
                  <select
                    value={profileType}
                    onChange={(e) => setProfileType(e.target.value as ProfileType)}
                  >
                    <option value="">Любой</option>
                    {PROFILE_TYPES.map((pt) => (
                      <option key={pt} value={pt}>
                        {PROFILE_TYPE_LABEL[pt]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label>Форма</label>
                  <select
                    value={entityType}
                    onChange={(e) => setEntityType(e.target.value as EntityType)}
                  >
                    <option value="">Любая</option>
                    {ENTITY_TYPES.map((et) => (
                      <option key={et} value={et}>
                        {ENTITY_TYPE_LABEL[et]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>Должность на площадке</label>
                  <select
                    value={staffRole}
                    onChange={(e) => setStaffRole(e.target.value as StaffRole)}
                  >
                    <option value="">Любая</option>
                    {STAFF_ROLES.map((sr) => (
                      <option key={sr} value={sr}>
                        {STAFF_ROLE_LABEL[sr]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label>Проверка</label>
                  <select
                    value={verifyState}
                    onChange={(e) => setVerifyState(e.target.value as VerifyState)}
                  >
                    <option value="">Любая</option>
                    {VERIFY_STATES.map((v) => (
                      <option key={v} value={v}>
                        {VERIFY_STATE_LABEL[v]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>Состояние</label>
                  <select
                    value={banned}
                    onChange={(e) => setBanned(e.target.value as '' | 'yes' | 'no')}
                  >
                    <option value="">Все</option>
                    <option value="no">Активные</option>
                    <option value="yes">Заблокированные</option>
                  </select>
                </div>
                <div className="field">
                  <label>Заходил</label>
                  <select
                    value={activeDays}
                    onChange={(e) =>
                      setActiveDays(e.target.value ? Number(e.target.value) : '')
                    }
                  >
                    <option value="">Когда угодно</option>
                    <option value="7">За неделю</option>
                    <option value="30">За месяц</option>
                    <option value="90">За три месяца</option>
                  </select>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label>Допуск</label>
                  <select
                    value={active}
                    onChange={(e) => setActive(e.target.value as '' | 'yes' | 'no')}
                  >
                    <option value="">Все</option>
                    <option value="yes">Допущенные</option>
                    <option value="no">На модерации / деактивированные</option>
                  </select>
                </div>
              </div>

              <label className="check">
                <input
                  type="checkbox"
                  checked={withCompany}
                  onChange={(e) => setWithCompany(e.target.checked)}
                />
                Только с привязанной компанией
              </label>

              {userFilters > 0 && (
                <button
                  className="ghost"
                  style={{ marginTop: 'var(--s3)' }}
                  onClick={() => {
                    setRole('');
                    setProfileType('');
                    setEntityType('');
                    setStaffRole('');
                    setVerifyState('');
                    setBanned('');
                    setActive('');
                    setWithCompany(false);
                    setActiveDays('');
                  }}
                >
                  Сбросить фильтры
                </button>
              )}
            </div>
          )}

          <div className="meta" style={{ margin: 'var(--s3) 0 var(--s2)' }}>
            Найдено: {total}
          </div>

          {loading && <div className="empty">Загрузка…</div>}
          {!loading && users.length === 0 && <div className="empty">Никого не нашлось.</div>}

          {/* Строка, а не карточка: список участников листают, чтобы найти
              одного нужного, и на экран должно помещаться десять человек,
              а не полтора. Всё лишнее живёт в развороте. */}
          {/* Шапка колонок — только на широком экране. Ради неё и
              зафиксированы ширины: плашки состояния должны выстраиваться
              в столбец, иначе их не просмотреть глазом сверху вниз. */}
          {/*
            * Список — общий примитив `AdminList`, а не своя вёрстка.
            *
            * У восьми вкладок админки было по своей: правка отступа в
            * одной не доезжала до остальных, и они расходились. Теперь
            * вкладка описывается колонками, а оформление одно на всех.
            *
            * В строке ровно одно действие — «Открыть». Три кнопки на
            * карточку и сделали прежний экран тесным: помещалось три
            * человека там, где помещается одиннадцать.
            */}
          <AdminList
            rows={users}
            keyOf={(u) => u.id}
            empty="Никого не нашлось"
            highlight={(u) => !u.active || u.banned}
            action={{
              label: 'Открыть',
              onClick: (u) => {
                // Поле пароля чистим при переключении: набранное для
                // одного человека не должно уехать другому.
                setNewPassword('');
                setUserTab('profile');
                setExpanded(u.id);
              },
            }}
            columns={[
              {
                title: 'Участник',
                cell: (u) => (
                  <>
                    <strong>
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.login || '—'}
                    </strong>
                    <span className="meta" style={{ display: 'block' }}>
                      {u.login ? `@${u.login}` : 'без логина'}
                      {u.phone && ` · ${u.phone}`}
                      {u.company && ` · ${u.company.name}`}
                    </span>
                  </>
                ),
              },
              {
                title: 'Состояние',
                // Шире, чем кажется нужным: у сотрудника плашек три
                // («Модератор», «Экспедитор», «ИП»), и на переносе строка
                // становится выше соседних — столбец перестаёт читаться
                // сверху вниз, ради чего он и заведён.
                width: '300px',
                cell: (u) => (
                  <span className="urow__tags">
                    {u.staffRole !== 'NONE' && (
                      <span className="badge badge--verified">
                        {STAFF_ROLE_LABEL[u.staffRole]}
                      </span>
                    )}
                    {u.banned && <span className="badge badge--warn">бан</span>}
                    {!u.active && <span className="badge badge--accent">не допущен</span>}
                    {u.verifyState === 'APPROVED' && (
                      <span className="badge badge--verified">проверен</span>
                    )}
                    {u.verifyState === 'PENDING' && (
                      <span className="badge badge--accent">на проверке</span>
                    )}
                    {u.profileType && (
                      <span className="badge">{PROFILE_TYPE_LABEL[u.profileType]}</span>
                    )}
                    <span className="badge">{ENTITY_TYPE_LABEL[u.entityType]}</span>
                  </span>
                ),
              },
              {
                title: 'Заявок',
                inline: true,
                width: '80px',
                labelOnPhone: true,
                cell: (u) => <span className="tabular">{u.cargosPublished}</span>,
              },
              {
                title: 'Сделок',
                inline: true,
                width: '80px',
                labelOnPhone: true,
                cell: (u) => <span className="tabular">{u.ordersDone}</span>,
              },
              {
                title: 'Рейтинг',
                inline: true,
                width: '90px',
                labelOnPhone: true,
                cell: (u) => (
                  <span className="tabular">
                    {u.ratingCount > 0 ? `★ ${u.rating.toFixed(1)}` : '—'}
                  </span>
                ),
              },
              {
                title: 'Заходил',
                inline: true,
                width: '130px',
                labelOnPhone: true,
                cell: (u) => (
                  // Присутствие важнее рейтинга при разборе обращения:
                  // «не отвечает» и «не заходил месяц» — разные разговоры.
                  <span className="tabular" data-tip={seenTip(u)}>
                    {sinceLabel(u.lastSeenAt)}
                  </span>
                ),
              },
            ]}
          />

          {/*
            * Разбор — окно на вебе и шторка на телефоне, одно и то же
            * содержимое. Раньше он раскрывался прямо в строке и уводил
            * соседние строки вниз: найденный человек уезжал с экрана в
            * тот момент, когда его открывали.
            */}
          <AdminSheet
            open={openUser !== null}
            onClose={() => setExpanded(null)}
            title={
              openUser
                ? [openUser.firstName, openUser.lastName].filter(Boolean).join(' ') ||
                  openUser.login ||
                  'Участник'
                : ''
            }
            subtitle={
              openUser && (
                <>
                  {openUser.login ? `@${openUser.login}` : 'без логина'}
                  {openUser.company && ` · ${openUser.company.name}`}
                  {openUser.banned && openUser.banReason && (
                    <div style={{ color: 'var(--danger)' }}>
                      Причина бана: {openUser.banReason}
                    </div>
                  )}
                </>
              )
            }
            actions={
              openUser && (
                <>
                  {/*
                    * Внизу — то, ради чего разбор открывают чаще всего, и
                    * оно не уезжает при прокрутке. Остальное осталось во
                    * вкладке «Действия»: пятнадцать кнопок в закреплённой
                    * полосе — это уже не полоса, а второй экран.
                    */}
                  <button className="secondary" onClick={() => setExpanded(null)}>
                    Закрыть
                  </button>
                  {!openUser.active && (
                    <button
                      disabled={busy === openUser.id}
                      onClick={() => void toggleActive(openUser)}
                    >
                      Допустить на площадку
                    </button>
                  )}
                  <button
                    className={openUser.banned ? 'secondary' : 'danger'}
                    disabled={busy === openUser.id}
                    onClick={() => void toggleBan(openUser)}
                  >
                    {openUser.banned ? 'Разблокировать' : 'Заблокировать'}
                  </button>
                </>
              )
            }
          >
            {openUser && (
          <div style={{ marginTop: 12 }}>
            {/*
             * Три вкладки вместо одного длинного столбца: видно только
             * то, зачем сюда пришли, — избыток полей на экране
             * отвлекает от того одного, ради которого карточку открыли.
             */}
            <div className="seg" style={{ marginBottom: 'var(--s4)' }}>
              <button
                type="button"
                className={userTab === 'profile' ? 'seg__btn seg__btn--on' : 'seg__btn'}
                onClick={() => setUserTab('profile')}
              >
                Профиль
              </button>
              <button
                type="button"
                className={userTab === 'contacts' ? 'seg__btn seg__btn--on' : 'seg__btn'}
                onClick={() => setUserTab('contacts')}
              >
                Контакты
              </button>
              <button
                type="button"
                className={userTab === 'access' ? 'seg__btn seg__btn--on' : 'seg__btn'}
                onClick={() => setUserTab('access')}
              >
                Доступ
              </button>
            </div>

            {userTab === 'profile' && (
              <>
                <div className="field">
                  <label>Профиль деятельности</label>
                  <select
                    value={openUser.profileType ?? ''}
                    disabled={busy === openUser.id}
                    onChange={(e) =>
                      void patchUser(openUser, {
                        profileType: e.target.value as ProfileType,
                      })
                    }
                  >
                    <option value="" disabled>
                      не выбран
                    </option>
                    {PROFILE_TYPES.map((p) => (
                      <option key={p} value={p}>
                        {PROFILE_TYPE_LABEL[p]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Роль</label>
                  <select
                    value={openUser.role}
                    disabled={busy === openUser.id}
                    onChange={(e) => void patchUser(openUser, { role: e.target.value as Role })}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Должность в команде площадки. Полный доступ ею
                    не выдаётся — для этого отдельная кнопка на вкладке
                    «Доступ»: две дороги к одному праву кончаются лишним
                    доступом. */}
                <div className="field">
                  <label>Должность на площадке</label>
                  <select
                    value={openUser.staffRole}
                    disabled={busy === openUser.id}
                    onChange={(e) =>
                      void patchUser(openUser, { staffRole: e.target.value as StaffRole })
                    }
                  >
                    {STAFF_ROLES.map((sr) => (
                      <option key={sr} value={sr}>
                        {STAFF_ROLE_LABEL[sr]}
                      </option>
                    ))}
                  </select>
                  <span className="meta">{STAFF_ROLE_HINT[openUser.staffRole]}</span>
                </div>

                {/* Проверка — не блокировка: заблокированный наказан,
                    непроверенный просто ещё не рассмотрен. */}
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Проверка профиля</label>
                  <div className="chips">
                    {VERIFY_STATES.map((v) => (
                      <button
                        key={v}
                        className={openUser.verifyState === v ? 'chip chip--on' : 'chip'}
                        disabled={busy === openUser.id}
                        onClick={() => void decideVerify(openUser, v)}
                      >
                        {VERIFY_STATE_LABEL[v]}
                      </button>
                    ))}
                  </div>
                  {openUser.verifyNote && (
                    <span className="meta">Причина: {openUser.verifyNote}</span>
                  )}
                </div>
              </>
            )}

            {userTab === 'contacts' && (
              <>
                <div className="field-row">
                  <div className="field">
                    <label>Логин</label>
                    <input
                      defaultValue={openUser.login ?? ''}
                      placeholder="не задан"
                      onBlur={(e) =>
                        e.target.value !== (openUser.login ?? '') &&
                        void patchContacts(openUser, { login: e.target.value })
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Telegram</label>
                    <input
                      defaultValue={openUser.tgUsername ?? ''}
                      placeholder="без @"
                      onBlur={(e) =>
                        e.target.value !== (openUser.tgUsername ?? '') &&
                        void patchContacts(openUser, { tgUsername: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field">
                    <label>Почта</label>
                    <input
                      defaultValue={openUser.email ?? ''}
                      placeholder="не задана"
                      onBlur={(e) =>
                        e.target.value !== (openUser.email ?? '') &&
                        void patchContacts(openUser, { email: e.target.value })
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Телефон</label>
                    <input
                      defaultValue={openUser.phone ?? ''}
                      placeholder="+79001234567"
                      onBlur={(e) =>
                        e.target.value !== (openUser.phone ?? '') &&
                        void patchContacts(openUser, { phone: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Пароль задаётся вручную. Нет логина — сначала он:
                    без логина паролем всё равно не войти. */}
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>{openUser.hasPassword ? 'Новый пароль' : 'Задать пароль'}</label>
                  <div className="field-row">
                    <input
                      type="text"
                      autoComplete="off"
                      placeholder="от 8 символов"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                      className="secondary inline"
                      disabled={busy === openUser.id || newPassword.length < 8}
                      onClick={() => void setPassword(openUser)}
                    >
                      Применить
                    </button>
                  </div>
                  <span className="meta">
                    {openUser.login
                      ? 'Пароль показывается один раз — в базе только хеш.'
                      : 'Сначала задайте логин выше: без него пароль бесполезен.'}
                  </span>
                </div>
              </>
            )}

            {userTab === 'access' && (
              <>
                <div className="action-row">
                  <button
                    className="secondary btn--compact"
                    disabled={busy === openUser.id}
                    onClick={() => void patchUser(openUser, { isAdmin: !openUser.isAdmin })}
                  >
                    {openUser.isAdmin ? 'Снять права администратора' : 'Сделать администратором'}
                  </button>

                  {openUser.hasPassword && (
                    <button
                      className="secondary btn--compact"
                      disabled={busy === openUser.id}
                      onClick={() => void resetPassword(openUser)}
                    >
                      Сбросить пароль
                    </button>
                  )}

                  {/* Администратора так не смотрят: это был бы способ
                      получить чужие права без всякого следа. */}
                  {!openUser.isAdmin && (
                    <button
                      className="secondary btn--compact"
                      disabled={busy === openUser.id}
                      onClick={() => void viewAs(openUser)}
                    >
                      Смотреть его глазами
                    </button>
                  )}

                  <button
                    className={openUser.active ? 'danger btn--compact' : 'secondary btn--compact'}
                    disabled={busy === openUser.id}
                    onClick={() => void toggleActive(openUser)}
                  >
                    {openUser.active ? 'Деактивировать' : 'Активировать'}
                  </button>

                  <button
                    className={openUser.banned ? 'secondary btn--compact' : 'danger btn--compact'}
                    disabled={busy === openUser.id}
                    onClick={() => void toggleBan(openUser)}
                  >
                    {openUser.banned ? 'Разблокировать' : 'Заблокировать'}
                  </button>
                </div>

                <div className="danger-zone">
                  <div className="danger-zone__title">Опасная зона</div>
                  <div className="meta" style={{ marginBottom: 'var(--s2)' }}>
                    Отклики, файлы и расходы удалятся вместе с пользователем.
                    Действие необратимо.
                  </div>
                  <button
                    className="danger btn--compact"
                    onClick={async () => {
                      if (
                        !(await confirm(
                          `Удалить ${openUser.login ?? openUser.firstName ?? 'пользователя'} полностью? ` +
                            'Отклики, файлы и расходы уйдут вместе с ним.',
                        ))
                      ) {
                        return;
                      }
                      try {
                        await api.deleteUser(openUser.id);
                        await loadUsers();
                      } catch (e) {
                        await alert((e as Error).message);
                      }
                    }}
                  >
                    Удалить пользователя
                  </button>
                </div>
              </>
            )}
          </div>
            )}
          </AdminSheet>

          {/* Подгрузка курсором, а не страницы: список живой, и «страница 7»
              через минуту показывает уже других людей. */}
          {cursor && (
            <button
              className="secondary"
              style={{ marginTop: 'var(--s3)' }}
              disabled={more}
              onClick={() => void loadUsers(true)}
            >
              {more ? 'Загружаю…' : 'Показать ещё'}
            </button>
          )}
        </>
      )}

      {openTab === 'companies' && (
        <>
          {/*
            * Фильтр состояния набором, а не тумблером «только
            * непроверенные». Прежний признак был включён по умолчанию, и
            * вкладка открывалась показывая часть списка: «компании
            * пропали» было обычной жалобой. Состояний тут не два, и они
            * не отрицают друг друга.
            *
            * «Без владельца» стоит в том же ряду не случайно: это не
            * свойство проверки, а самая частая причина зайти в раздел —
            * в такой компании некому нанимать людей и раздавать права.
            */}
          <AdminFilters
            active={companyState === '' ? 0 : 1}
            search={
              <input
                placeholder="Название или ИНН"
                value={companyQuery}
                onChange={(e) => setCompanyQuery(e.target.value)}
              />
            }
          >
            {COMPANY_STATES.map((st) => (
              <button
                key={st || 'all'}
                type="button"
                className={companyState === st ? 'chip chip--on' : 'chip'}
                onClick={() => setCompanyState(st)}
              >
                {COMPANY_STATE_LABEL[st]}
              </button>
            ))}
          </AdminFilters>

          {loading && <div className="empty">Загрузка…</div>}

          {!loading && (
            <AdminList
              rows={companies}
              keyOf={(c) => c.id}
              empty="Компаний не нашлось"
              // Компания без владельца подсвечена целиком: её ищут глазами
              // по списку, а не вычитывают из значков.
              highlight={(c) => !c.ownerName}
              action={{ label: 'Открыть', onClick: (c) => setExpanded(c.id) }}
              columns={[
                {
                  title: 'Компания',
                  cell: (c) => (
                    <>
                      <strong>{c.name}</strong>
                      {c.okved && (
                        <span className="meta" style={{ display: 'block' }}>
                          ОКВЭД {c.okved}
                        </span>
                      )}
                    </>
                  ),
                },
                {
                  title: 'ИНН',
                  width: '140px',
                  labelOnPhone: true,
                  inline: true,
                  cell: (c) => <span className="tabular">{c.inn}</span>,
                },
                {
                  title: 'Владелец',
                  width: '190px',
                  labelOnPhone: true,
                  cell: (c) =>
                    c.ownerName ? (
                      <span>{c.ownerName}</span>
                    ) : (
                      <strong style={{ color: 'var(--warn)' }}>владельца нет</strong>
                    ),
                },
                {
                  title: 'Состояние',
                  width: '260px',
                  cell: (c) => (
                    <span className="urow__tags">
                      {c.verified ? (
                        <span className="badge badge--verified">проверена</span>
                      ) : (
                        <span className="badge">на модерации</span>
                      )}
                      {c.isDirectShipper ? (
                        <span className="badge badge--direct">прямой</span>
                      ) : (
                        <span className="badge badge--warn">посредник</span>
                      )}
                    </span>
                  ),
                },
                {
                  title: 'Заявок',
                  width: '80px',
                  labelOnPhone: true,
                  inline: true,
                  cell: (c) => <span className="tabular">{c.cargosCount}</span>,
                },
              ]}
            />
          )}

          <AdminSheet
            open={openCompany !== null}
            onClose={() => setExpanded(null)}
            title={openCompany?.name ?? ''}
            subtitle={
              openCompany && (
                <>
                  ИНН {openCompany.inn}
                  {openCompany.okved && ` · ОКВЭД ${openCompany.okved}`}
                  <div>
                    оформлено в компании {openCompany.membersCount} · привязано по
                    ИНН {openCompany.usersCount} · заявок {openCompany.cargosCount}
                  </div>
                </>
              )
            }
            actions={
              <button className="secondary" onClick={() => setExpanded(null)}>
                Закрыть
              </button>
            }
          >
            {openCompany && (
              <>
                {/*
                  * Переключатель вместо кнопки-состояния.
                  *
                  * «Пометить как посредника» говорит о будущем, а кто эта
                  * компания сейчас — приходится выводить из надписи.
                  * Переключатель показывает то, что есть, и меняет одним
                  * нажатием.
                  */}
                <div className="field">
                  <label>Проверка</label>
                  <div className="seg">
                    {[true, false].map((v) => (
                      <button
                        key={String(v)}
                        type="button"
                        className={
                          openCompany.verified === v ? 'seg__btn seg__btn--on' : 'seg__btn'
                        }
                        disabled={busy === openCompany.id}
                        onClick={() =>
                          openCompany.verified !== v &&
                          void patchCompany(openCompany, { verified: v })
                        }
                      >
                        {v ? 'Проверена' : 'На модерации'}
                      </button>
                    ))}
                  </div>
                  <span className="meta">
                    Проверка означает, что ИНН и полномочия смотрели руками.
                  </span>
                </div>

                <div className="field">
                  <label>Кто она на рынке</label>
                  <div className="seg">
                    {[true, false].map((v) => (
                      <button
                        key={String(v)}
                        type="button"
                        className={
                          openCompany.isDirectShipper === v
                            ? 'seg__btn seg__btn--on'
                            : 'seg__btn'
                        }
                        disabled={busy === openCompany.id}
                        onClick={() =>
                          openCompany.isDirectShipper !== v &&
                          void patchCompany(openCompany, { isDirectShipper: v })
                        }
                      >
                        {v ? 'Прямой грузовладелец' : 'Посредник'}
                      </button>
                    ))}
                  </div>
                  <span className="meta">
                    Ставится по ОКВЭД автоматически, но решает человек.
                  </span>
                </div>

                <div className="divider" />
                <AdminCompanyMembers companyId={openCompany.id} />
              </>
            )}
          </AdminSheet>
        </>
      )}
      </div>
    </div>
  );
}
